
import { useState, useCallback, useEffect } from 'react';
import { App } from 'antd';
import api from '../../../../../services/api';
import type { RufusQA } from '../types';

export const useRufusQA = (skuId: number, asin?: string) => {
    const { message } = App.useApp();
    const [loading, setLoading] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [categorizing, setCategorizing] = useState(false);
    const [qaList, setQaList] = useState<RufusQA[]>([]);
    const [analysisResult, setAnalysisResult] = useState<string>('');

    // 加载问答数据
    const loadQAs = useCallback(async () => {
        if (!skuId) return;

        setLoading(true);
        try {
            const response = await api.get(`/rufus-qa/by-sku/${skuId}/`);
            setQaList(response.data.results || []);
        } catch (error) {
            console.error('加载Rufus问答失败:', error);
        } finally {
            setLoading(false);
        }
    }, [skuId]);

    // 加载最新的分析报告
    const loadReport = useCallback(async () => {
        if (!skuId) return;
        try {
            const response = await api.get(`/rufus-qa/report/${skuId}/`);
            if (response.data.found) {
                setAnalysisResult(response.data.report);
            }
        } catch (error) {
            console.error('加载分析报告失败:', error);
        }
    }, [skuId]);

    useEffect(() => {
        loadQAs();
        loadReport();
    }, [loadQAs, loadReport]);

    // AI 自动分类
    const handleCategorize = async () => {
        if (!skuId) return;
        setCategorizing(true);
        try {
            const response = await api.post('/rufus-qa/categorize/', { sku_id: skuId });
            if (response.data.success) {
                message.success(`分类完成，更新了 ${response.data.updated} 条数据`);
                loadQAs();
            } else {
                message.warning(response.data.message || '分类未成功');
            }
        } catch (error) {
            console.error('分类失败:', error);
            message.error('分类失败，请查看控制台');
        } finally {
            setCategorizing(false);
        }
    };

    // AI 分析
    const handleAnalyze = async () => {
        if (!skuId && !asin) return;
        if (qaList.length === 0) {
            message.warning('请先抓取问答数据');
            return;
        }

        setAnalyzing(true);
        try {
            const response = await api.post('/rufus-qa/analyze/', {
                sku_id: skuId,
                asin: asin
            }, { timeout: 300000 }); // 5分钟超时

            if (response.data.success && response.data.report) {
                setAnalysisResult(response.data.report);
                message.success('分析完成');
            }
        } catch (error: any) {
            console.error('分析失败:', error);
            message.error('AI分析失败: ' + (error.response?.data?.error || '服务器错误'));
        } finally {
            setAnalyzing(false);
        }
    };

    // 删除单条问答
    const handleDelete = async (id: number) => {
        try {
            await api.delete(`/rufus-qa/${id}/`);
            message.success('删除成功');
            setQaList(prev => prev.filter(qa => qa.id !== id));
        } catch (error) {
            console.error('删除失败:', error);
            message.error('删除失败');
        }
    };

    // 清空问答
    const handleClear = async () => {
        try {
            await api.delete(`/rufus-qa/clear/${skuId}/`);
            message.success('已清空所有问答');
            setQaList([]);
            setAnalysisResult('');
        } catch (error) {
            message.error('清空失败');
        }
    };

    return {
        loading,
        analyzing,
        categorizing,
        qaList,
        analysisResult,
        loadQAs,
        handleCategorize,
        handleAnalyze,
        handleDelete,
        handleClear
    };
};
